import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  DialogContent,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import type { WaveForm } from '@/generated/graphql';
import { useAuth } from '@/common/auth/hooks/useAuth';
import { useTrackComments } from '@/hooks/useTrackComments';
import { useProjectPermissions } from '@/hooks/projectPermissions';
import { useProjectDetails } from '@/common/components/projectDetails/context/ProjectDetailsContext';
import DialogWindow from '@/common/components/_misc/DialogWindow';
import { ReviewWaveform, type ReviewWaveformHandle } from './ReviewWaveform';
import { CommentComposer } from './CommentComposer';
import { CommentThreadList } from './CommentThreadList';
import { createTimeSource } from './timeSource';

interface WaveformCommentsDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  /** Version being heard; new comments are stamped with it. */
  versionNumber: number;
  /** The version's preview file; null when it has none. */
  preview: {
    _id: string;
    fileName: string;
    waveForm?: WaveForm | null;
  } | null;
  /** Thread to highlight when the dialog opens (e.g. from a banner marker). */
  initialHighlightId?: string | null;
  /** Playhead (ms) the dialog starts at, e.g. the banner's position. */
  initialTimeMs?: number;
}

export function WaveformCommentsDialog({
  open,
  onClose,
  projectId,
  versionNumber,
  preview,
  initialHighlightId,
  initialTimeMs,
}: WaveformCommentsDialogProps) {
  const theme = useTheme();
  const { project } = useProjectDetails();
  const perms = useProjectPermissions(project);
  const { userId } = useAuth();
  const comments = useTrackComments({ projectId, versionNumber });

  const waveformRef = useRef<ReviewWaveformHandle>(null);
  // Playhead lives in an external store so ticks never re-render this dialog
  // or the thread list; only the composer's time labels subscribe.
  const timeSource = useMemo(
    () => createTimeSource(initialTimeMs ?? 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fresh store per preview
    [preview?._id]
  );
  const onTimeChange = timeSource.set;
  // Notes on the version being heard first, then older versions newest-first;
  // the sort is stable, so server order (by time) holds within a version.
  const orderedThreads = useMemo(
    () =>
      [...comments.threads].sort(
        (a, b) =>
          Number(b.versionNumber === versionNumber) -
            Number(a.versionNumber === versionNumber) ||
          (b.versionNumber ?? 0) - (a.versionNumber ?? 0)
      ),
    [comments.threads, versionNumber]
  );
  const [highlight, setHighlight] = useState<{
    id: string;
    nonce: number;
  } | null>(initialHighlightId ? { id: initialHighlightId, nonce: 0 } : null);
  const seekToMs = useCallback((ms: number) => {
    waveformRef.current?.seekToMs(ms);
  }, []);

  const onMarkerClick = useCallback((id: string) => {
    setHighlight((h) => ({ id, nonce: (h?.nonce ?? 0) + 1 }));
  }, []);

  // Resolvers can be project members who never commented, so names come
  // from the project roster as a fallback for the "Resolved by" line.
  const memberNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const m of project?.members ?? []) {
      if (!m?.userId) continue;
      const name =
        m.artistName || (m.userEmail ? m.userEmail.split('@')[0] : '');
      if (name) names[m.userId] = name;
    }
    return names;
  }, [project]);

  const hasWaveform = !!preview?.waveForm && preview.waveForm.duration > 0;
  const versionThreads = useMemo(
    () => comments.threads.filter((t) => t.versionNumber === versionNumber),
    [comments.threads, versionNumber]
  );
  const openCount = versionThreads.filter((t) => !t.resolvedAt).length;
  const resolvedCount = versionThreads.length - openCount;
  const otherOpenCount = comments.threads.filter(
    (t) => t.versionNumber !== versionNumber && !t.resolvedAt
  ).length;

  return (
    <DialogWindow
      open={open}
      onClose={onClose}
      title="Comments"
      closeDataCy="close-comments-dialog"
      maxWidth="md"
      fullWidth
    >
      <DialogContent sx={{ [theme.breakpoints.down('md')]: { pt: 0 } }}>
        <Typography
          variant="body2"
          color="text.secondary"
          data-cy="comment-dialog-subtitle"
          sx={{ mb: 2, [theme.breakpoints.down('md')]: { pt: 2 } }}
        >
          {`${preview?.fileName ?? `Version ${versionNumber}`} · ${openCount} open · ${resolvedCount} resolved${otherOpenCount > 0 ? ` · ${otherOpenCount} in other versions` : ''}`}
        </Typography>
        {comments.loading && (
          <Box
            role="status"
            aria-busy="true"
            aria-label="Loading comments"
            sx={{ mb: 2 }}
          >
            <CircularProgress size={20} />
          </Box>
        )}
        {comments.error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            data-cy="comment-load-error"
            action={
              <Button
                color="inherit"
                size="small"
                data-cy="comment-retry"
                onClick={() => void comments.refetch()}
              >
                Retry
              </Button>
            }
          >
            {comments.error.message}
          </Alert>
        )}
        <Box
          data-cy="comment-sticky-header"
          sx={{
            [theme.breakpoints.down('md')]: {
              position: 'sticky',
              top: 0,
              zIndex: 2,
              bgcolor: 'background.paper',
              pt: 1,
              pb: 1,
              boxShadow: 1,
            },
          }}
        >
          {preview?.waveForm && (
            <ReviewWaveform
              ref={waveformRef}
              waveForm={preview.waveForm}
              projectId={projectId}
              fileId={preview._id}
              threads={versionThreads}
              onTimeChange={onTimeChange}
              onMarkerClick={onMarkerClick}
              initialTimeMs={initialTimeMs}
            />
          )}
          {perms.canComment &&
            !!preview &&
            (hasWaveform ? (
              <CommentComposer
                timeSource={timeSource}
                onSubmit={comments.create}
                maxMs={Math.ceil(preview!.waveForm!.duration * 1000)}
              />
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                data-cy="comment-no-waveform"
                sx={{ my: 2 }}
              >
                Comments are anchored to the audio, so adding new ones needs a
                waveform for this version's preview.
              </Typography>
            ))}
        </Box>
        {!comments.loading &&
          !comments.error &&
          comments.threads.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              data-cy="comment-empty"
              sx={{ py: 2 }}
            >
              No comments yet. Click the waveform and add the first note.
            </Typography>
          )}
        <CommentThreadList
          threads={orderedThreads}
          userId={userId}
          canComment={perms.canComment}
          canResolveComments={perms.canResolveComments}
          isOwner={perms.isOwner}
          highlightedThreadId={highlight?.id}
          highlightNonce={highlight?.nonce}
          selectedVersion={versionNumber}
          seekToMs={seekToMs}
          onReply={comments.reply}
          onEdit={comments.update}
          onDelete={comments.remove}
          onResolve={comments.resolve}
          onReopen={comments.reopen}
          memberNames={memberNames}
        />
      </DialogContent>
    </DialogWindow>
  );
}
